import React, { useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { NavigationItem } from './types';
import { isRouteActive } from './config';
import {
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface NavSidebarProps {
  items: NavigationItem[];
  className?: string;
  collapsed?: boolean;
  onNavigate?: (href: string) => void;
  // If provided, items whose id is included will always render expanded children
  alwaysExpandedIds?: string[];
}

const NavSidebar: React.FC<NavSidebarProps> = ({
  items,
  className,
  collapsed = false,
  onNavigate,
  alwaysExpandedIds = [],
}) => {
  const location = useLocation();
  
  // Add active state to items based on current route
  const navItems = useMemo(() => {
    return items.map(item => ({
      ...item,
      isActive: isRouteActive(item.href, location.pathname),
      children: item.children?.map(child => ({
        ...child,
        isActive: isRouteActive(child.href, location.pathname)
      }))
    }));
  }, [items, location.pathname]);

  // Handle item click
  const handleClick = (href: string) => {
    if (onNavigate) {
      onNavigate(href);
    }
  };

  return (
    <div 
      className={cn(
        "flex flex-col h-full py-4 bg-background transition-all",
        collapsed ? "w-16" : "w-64",
        className
      )}
    >
      <div className="px-4 py-2 mb-4">
        <h3 className={cn("font-semibold transition-opacity", 
          collapsed ? "opacity-0 w-0" : "opacity-100")}>
          Navigation
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        <nav className="space-y-1 px-2">
          {navItems.map((item) => {
            // Determine if this item has active children
            const hasActiveChild = item.children?.some(child => child.isActive);
            const alwaysExpanded = alwaysExpandedIds.includes(item.id);
            
            // Disabled state styling
            const isDisabled = item.disabled;
            const itemContent = (
              <div
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md transition-colors",
                  item.isActive ? "bg-[#272729] text-[#D7DADC]" : "text-[#818384] hover:bg-[#272729] hover:text-[#D7DADC]",
                  isDisabled && "opacity-50 cursor-not-allowed",
                  hasActiveChild && "bg-[#272729]/40 text-[#D7DADC]"
                )}
              >
                <item.icon size={20} />
                <span className={cn("transition-all", collapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100")}>
                  {item.label}
                </span>
                {item.badge && (
                  <span className={cn(
                    "ml-auto text-xs font-medium px-2 py-0.5 rounded-full",
                    typeof item.badge === 'string' 
                      ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" 
                      : "bg-red-500 text-white"
                  )}>
                    {typeof item.badge === 'number' ? item.badge : typeof item.badge === 'string' ? item.badge : ''}
                  </span>
                )}
                {item.children && item.children.length > 0 && !collapsed && (
                  <span className="ml-auto">
                    {item.isActive || hasActiveChild || alwaysExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </span>
                )}
              </div>
            );
            
            // Wrap with tooltip if collapsed
            const navItem = collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  {isDisabled ? (
                    <div>{itemContent}</div>
                  ) : (
                    <Link 
                      to={item.href} 
                      onClick={(e) => {
                        if (isDisabled) {
                          e.preventDefault();
                          return;
                        }
                        handleClick(item.href);
                      }}
                    >
                      {itemContent}
                    </Link>
                  )}
                </TooltipTrigger>
                <TooltipContent side="right">
                  {item.label}
                </TooltipContent>
              </Tooltip>
            ) : (
              isDisabled ? (
                <div>{itemContent}</div>
              ) : (
                <Link 
                  to={item.href} 
                  onClick={(e) => {
                    if (isDisabled) {
                      e.preventDefault();
                      return;
                    }
                    handleClick(item.href);
                  }}
                >
                  {itemContent}
                </Link>
              )
            );
            
            // Use a div with key instead of React.Fragment to avoid prop forwarding issues
            return (
              <div key={item.id} className="contents">
                {navItem}
                
                {/* Render children if expanded and this item has children */}
                {!collapsed && item.children && item.children.length > 0 && (item.isActive || hasActiveChild || alwaysExpanded) && (
                  <div className="pl-8 mt-1 space-y-1">
                    {item.children.map(child => (
                      <Link 
                        key={child.id}
                        to={child.href}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm",
                          child.isActive ? "bg-[#272729] text-[#D7DADC]" : "text-[#818384] hover:bg-[#272729] hover:text-[#D7DADC]",
                          child.disabled && "opacity-50 cursor-not-allowed"
                        )}
                        onClick={(e) => {
                          if (child.disabled) {
                            e.preventDefault();
                            return;
                          }
                          handleClick(child.href);
                        }}
                      >
                        <child.icon size={16} />
                        <span>{child.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default NavSidebar;
